import {
    normalizeRoleName,
    isAdminRole,
    isLytRole,
} from 'src/constants/roles.constants';
import { NAV_AUDIENCE, NAV_SECTIONS } from 'src/config/navigation.constants';
import { getEnvFeatureFlagDefaults } from 'src/config/featureFlags';
import { isFeatureFlagEnabled } from 'src/store/feature-flags/feature-flags.utils';

const EMPTY_SIDEBAR_NAV = Object.freeze({
    [NAV_SECTIONS.OVERVIEW]: [],
    [NAV_SECTIONS.TEAM_MANAGEMENT]: [],
    [NAV_SECTIONS.INTERNAL_TOOLS]: [],
    [NAV_SECTIONS.SETTINGS]: [],
});

/**
 * @typedef {Object} NavigationContext
 * @property {string|null} userRole - Legacy usertype or backend role_name
 * @property {string|null} agencyType - Selected agency's agency_type
 * @property {Record<string, boolean>} featureFlags - Resolved internal flag values
 */

export const buildNavigationContext = ({ userRole, agencyType, featureFlags }) => ({
    userRole: userRole || null,
    agencyType: agencyType || null,
    normalizedRole: normalizeRoleName(userRole),
    featureFlags: featureFlags || {},
});

/**
 * Feature-flag gate for a registry entry.
 * - `featureFlag`: required ON (omit = no requirement)
 * - `featureFlagOff`: required OFF (omit = no requirement)
 */
const isFeatureFlagAllowed = (entry, featureFlags) => {
    if (entry.featureFlagOff && isFeatureFlagEnabled(featureFlags, entry.featureFlagOff)) {
        return false;
    }
    return isFeatureFlagEnabled(featureFlags, entry.featureFlag);
};

const isAgencyTypeAllowed = (entry, agencyType) => {
    if (!entry.agencyTypes || entry.agencyTypes.length === 0) {
        return true;
    }
    if (!agencyType) {
        return true;
    }
    return entry.agencyTypes.includes(agencyType);
};

const isRoleAllowed = (entry, normalizedRole) => {
    if (!entry.allowedRoles) {
        return true;
    }
    if (!normalizedRole) {
        return false;
    }
    return entry.allowedRoles.includes(normalizedRole);
};

const isNavAudienceAllowed = (entry, context) => {
    const audience = entry.nav?.audience || NAV_AUDIENCE.ALL;

    switch (audience) {
    case NAV_AUDIENCE.ADMIN:
        return isAdminRole(context.userRole);
    case NAV_AUDIENCE.LYT_INTERNAL:
        return isLytRole(context.userRole);
    case NAV_AUDIENCE.ALL:
    default:
        return true;
    }
};

/**
 * Whether a registry entry's route should be registered for the current context.
 */
export const isRouteAccessible = (entry, context, { skipRoleCheck = false } = {}) => {
    if (!entry?.routePath || !entry?.component) {
        return false;
    }
    if (!isFeatureFlagAllowed(entry, context.featureFlags)) {
        return false;
    }
    if (!skipRoleCheck && !isRoleAllowed(entry, context.normalizedRole)) {
        return false;
    }
    return isAgencyTypeAllowed(entry, context.agencyType);
};

/**
 * Whether a registry entry should appear in the sidebar for the current context.
 */
export const isNavItemVisible = (entry, context, options = {}) => {
    if (!entry?.nav || !entry?.navPath) {
        return false;
    }
    if (!isRouteAccessible(entry, context, options)) {
        return false;
    }
    if (options.skipAudienceCheck) {
        return true;
    }
    return isNavAudienceAllowed(entry, context);
};

export const toSidebarNavItem = (entry) => ({
    title: entry.nav.title,
    key: entry.nav.key,
    href: entry.navPath,
    iconName: entry.nav.iconName,
    ...(entry.nav.noIconColor ? { noIconColor: true } : {}),
});

export const toRouteDefinition = (entry) => ({
    component: entry.component,
    path: entry.routePath,
    isDefault: false,
    allowedRoles: entry.allowedRoles,
    id: entry.id,
});

/**
 * @param {import('src/config/navigation.registry').NAV_REGISTRY} registry
 * @param {NavigationContext} context
 */
export const getAccessibleRoutes = (registry, context) =>
    registry
        .filter((entry) => isRouteAccessible(entry, context))
        .map(toRouteDefinition);

/**
 * @param {import('src/config/navigation.registry').NAV_REGISTRY} registry
 * @param {NavigationContext} context
 * @returns {typeof EMPTY_SIDEBAR_NAV}
 */
export const getSidebarNavBySection = (registry, context) => {
    const sections = {
        [NAV_SECTIONS.OVERVIEW]: [],
        [NAV_SECTIONS.TEAM_MANAGEMENT]: [],
        [NAV_SECTIONS.INTERNAL_TOOLS]: [],
        [NAV_SECTIONS.SETTINGS]: [],
    };

    registry.forEach((entry) => {
        if (!isNavItemVisible(entry, context)) {
            return;
        }
        const section = entry.nav.section;
        if (sections[section]) {
            sections[section].push(toSidebarNavItem(entry));
        }
    });

    return sections;
};

/**
 * Route path prefixes that are inaccessible for the given agency type.
 * Used when switching agencies to redirect users off restricted pages.
 */
export const getRestrictedRoutePrefixesForAgencyType = (registry, agencyType, featureFlags) => {
    if (!agencyType) {
        return [];
    }

    const flags = featureFlags || getEnvFeatureFlagDefaults();

    return registry
        .filter((entry) => entry.navPath && entry.routePath)
        .filter((entry) => isFeatureFlagAllowed(entry, flags))
        .filter((entry) => {
            if (!entry.agencyTypes || entry.agencyTypes.length === 0) {
                return false;
            }
            return !entry.agencyTypes.includes(agencyType);
        })
        .map((entry) => entry.navPath.split('/').filter(Boolean)[0])
        .filter(Boolean);
};

const getNavForAgencyTypeBySection = (registry, agencyType, section) =>
    registry
        .filter((entry) => entry.nav?.section === section && entry.navPath)
        .filter((entry) => isFeatureFlagAllowed(entry, getEnvFeatureFlagDefaults()))
        .filter((entry) => isAgencyTypeAllowed(entry, agencyType))
        .map(toSidebarNavItem);

/** @deprecated Use getSidebarNavBySection — kept for legacy sidebar.config exports */
export const getOverviewNavForAgencyType = (registry, agencyType) =>
    getNavForAgencyTypeBySection(registry, agencyType, NAV_SECTIONS.OVERVIEW);

/** @deprecated Use getSidebarNavBySection — kept for legacy sidebar.config exports */
export const getSettingsNavForAgencyType = (registry, agencyType) =>
    getNavForAgencyTypeBySection(registry, agencyType, NAV_SECTIONS.SETTINGS);

/**
 * Context-aware default route that respects agency-type and feature-flag access.
 *
 * Preference order: live-operations → first accessible navPath/routePath.
 *
 * @param {import('src/config/navigation.registry').NAV_REGISTRY} registry
 * @param {NavigationContext} context
 * @returns {string}
 */
export const getDefaultRouteForContext = (registry, context) => {
    const accessible = registry.filter((entry) => isRouteAccessible(entry, context));

    const liveOps = accessible.find((e) => e.id === 'live-operations');
    if (liveOps?.navPath) {
        return liveOps.navPath;
    }

    const firstWithNav = accessible.find((e) => e.navPath);
    if (firstWithNav) {
        return firstWithNav.navPath;
    }

    const firstWithRoute = accessible.find((e) => e.routePath);
    if (firstWithRoute) {
        return firstWithRoute.routePath;
    }

    return '/';
};

export { EMPTY_SIDEBAR_NAV };
